/**
 * Anything that floats above the page, in one place.
 *
 * There were four of these — the info box, the notification tray, the hover
 * tooltip, the rail's flyout — each with its own positioning, its own idea of
 * which z-index was high enough, and its own bugs. Three of the four could be
 * clipped by a scrolling ancestor and two could open off the edge of the screen.
 *
 * The info box was the worst of them, and it is the one that got reported: it
 * clamped horizontally and not vertically, so a hint anywhere near the top of a
 * page opened *above the viewport* and could not be read at all. Being
 * `absolute`, it was also clipped by every table wrapper it sat inside.
 *
 * Two rules, and they are what this module exists to guarantee:
 *
 *  1. **Nothing that floats is ever clipped.** It is portalled out of the page
 *     tree and positioned `fixed`, so no ancestor's `overflow` can cut it off.
 *  2. **Nothing that floats is ever off screen.** Both axes are clamped to the
 *     viewport, and the preferred side flips when there is no room on it.
 */

/**
 * The stacking order, written down.
 *
 * It was previously four numbers scattered across four files — `z-30`, `z-40`,
 * `z-50`, `z-[60]` — and whether the info box appeared above a dialog was
 * whatever those happened to add up to. That is not a design, it is a
 * coincidence, and the coincidence broke.
 *
 * Read it as: things lower down interrupt things above them in this list.
 */
export const LAYERS = {
  /** The pinned rail and the sticky page header. Part of the page. */
  chrome: 30,
  /** Menus and dropdowns opened from the chrome — the rail's category flyout. */
  flyout: 40,
  /** Dialogs, and the scrim behind them. They own the screen while open. */
  dialog: 50,
  /**
   * Explanatory text: the info box, tooltips, hover cards.
   *
   * **Above dialogs, deliberately.** An info box exists to explain the thing
   * under the pointer, and inside a dialog the thing under the pointer is part
   * of the dialog. Ranking it below would make the one control that explains a
   * form unreadable on the form that most needs explaining.
   */
  explain: 70,
  /** Transient confirmations. Above everything, because they self-dismiss. */
  toast: 80,
} as const;

/**
 * Where portals go.
 *
 * The **React root**, not `document.body`. React 18 attaches its event
 * listeners to the root container, so a portal outside it receives no React
 * events at all — a panel's `onMouseEnter` never fires and a `Link` inside it
 * silently falls back to a full page reload. That cost an afternoon once.
 *
 * `#root` carries no transform, filter or containment, so `position: fixed`
 * inside it still resolves against the viewport, which is the only property of
 * `document.body` that mattered.
 */
export const portalHost = (): HTMLElement => document.getElementById('root') ?? document.body;

export type Side = 'top' | 'bottom' | 'left' | 'right';

export type Placement = { top: number; left: number; side: Side };

/**
 * Put a box of a known size next to an anchor, on screen.
 *
 * `size` may legitimately be zero on the first pass — the panel has to render
 * before it can be measured. Callers place it provisionally, measure, then call
 * again; the clamping below is written so a zero size still produces a sane
 * first position rather than a jump.
 */
export function anchorTo(
  anchor: DOMRect,
  size: { width: number; height: number },
  preferred: Side,
  gap = 8,
): Placement {
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  /*
   * Flip only on the axis in play.
   *
   * A box that wanted to be above and has no room becomes one below, not one to
   * the left. The old info box never flipped at all, which is precisely how it
   * ended up above the top of the screen.
   */
  let side = preferred;
  if (preferred === 'top' && anchor.top - size.height - gap < margin) side = 'bottom';
  else if (preferred === 'bottom' && anchor.bottom + size.height + gap > vh - margin) side = 'top';
  else if (preferred === 'left' && anchor.left - size.width - gap < margin) side = 'right';
  else if (preferred === 'right' && anchor.right + size.width + gap > vw - margin) side = 'left';

  let top: number;
  let left: number;

  switch (side) {
    case 'top':
      top = anchor.top - size.height - gap;
      left = anchor.left + anchor.width / 2 - size.width / 2;
      break;
    case 'bottom':
      top = anchor.bottom + gap;
      left = anchor.left + anchor.width / 2 - size.width / 2;
      break;
    case 'left':
      top = anchor.top + anchor.height / 2 - size.height / 2;
      left = anchor.left - size.width - gap;
      break;
    default:
      top = anchor.top + anchor.height / 2 - size.height / 2;
      left = anchor.right + gap;
  }

  /*
   * Then clamp both axes regardless of side.
   *
   * Flipping alone is not enough: a tall box beside a row near the bottom of the
   * window still hangs off it, and a wide box anchored to the last column of a
   * table still runs past the right edge. `Math.max(margin, …)` on the upper
   * bound keeps a box *larger than the viewport* at the top-left rather than at
   * a negative offset, which is the degenerate case on a short phone screen.
   */
  return {
    side,
    top: Math.min(Math.max(top, margin), Math.max(margin, vh - size.height - margin)),
    left: Math.min(Math.max(left, margin), Math.max(margin, vw - size.width - margin)),
  };
}

/**
 * Is any part of this box outside the window?
 *
 * For tests to assert against, so "always visible" is a measured property rather
 * than a claim in a comment. One pixel of tolerance, because a box clamped
 * exactly to the margin can round to a hair over.
 */
export function isFullyOnScreen(rect: DOMRect): boolean {
  return (
    rect.top >= -1 &&
    rect.left >= -1 &&
    rect.bottom <= window.innerHeight + 1 &&
    rect.right <= window.innerWidth + 1
  );
}
